import React, { useState, useRef, useEffect, useCallback } from 'react';
import { DoughnutChart } from '../components/DoughnutChart';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useAuth } from '../hooks/useAuth';
import { useAIDashboardOverview } from '../hooks/useApi';
import { aiDashboardApi } from '../api/client';
import type { ChatMessage, AIDashboardOverview } from '../api/client';
import { cn } from '../lib/utils';
import { formatRelativeTime } from '../lib/utils';
import { Spinner } from '../components/ui/Spinner';

// ─── Helpers ────────────────────────────────────────────────────────────────

function getGreeting(): string {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  return 'Good evening';
}

function formatCurrency(n: number): string {
  return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(n);
}

function formatDateLong(): string {
  return new Intl.DateTimeFormat('en-GB', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(new Date());
}

// ─── Sub-components ─────────────────────────────────────────────────────────

const StatusBadge: React.FC<{
  status: 'CRITICAL' | 'WARNING' | 'OK' | 'URGENT' | 'HIGH' | 'MEDIUM';
}> = ({ status }) => {
  const styles: Record<string, string> = {
    CRITICAL: 'bg-error/10 text-error',
    URGENT: 'bg-error/10 text-error',
    WARNING: 'bg-warning/10 text-[#A08040] dark:text-warning',
    HIGH: 'bg-warning/10 text-[#A08040] dark:text-warning',
    OK: 'bg-success/10 text-success',
    MEDIUM: 'bg-primary/10 text-primary',
  };
  return (
    <span className={cn('inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold tracking-wide', styles[status] || styles.OK)}>
      {status}
    </span>
  );
};

// ─── Insight Cards (5 cards matching v3 mockup) ────────────────────────────

const InsightCards: React.FC<{ data: AIDashboardOverview['insightCards'] }> = ({ data }) => {
  const { yesterdayRecap, overnightAlerts, channelRisk, trending, dataHealth } = data;
  const deliverooRisk = data.deliverooRisk;
  const hasChannelRisk = channelRisk.zeroStockCount > 0 || channelRisk.negativeStockCount > 0;

  return (
    <div className="flex gap-3.5 overflow-x-auto pb-1.5 mb-7 scrollbar-hide">
      {/* Card 1: Yesterday's Recap */}
      <div className="min-w-[195px] max-w-[230px] flex-1 shrink-0 p-4 rounded-2xl bg-white dark:bg-[#2A2928] border border-bronze-200 dark:border-[#3A3836] relative overflow-hidden card-hover">
        <div className="absolute top-0 left-0 right-0 h-[3px] bg-primary" />
        <p className="text-[10px] font-semibold uppercase tracking-wider text-primary mb-1">Yesterday's Recap</p>
        {yesterdayRecap.transactions > 0 ? (
          <>
            <p className="text-2xl font-bold text-text dark:text-[#E8E4DD] tracking-tight leading-tight">
              {formatCurrency(yesterdayRecap.revenue)}
            </p>
            <p className="text-[11px] text-text-muted dark:text-[#9B958E] mt-0.5">
              {yesterdayRecap.transactions} transactions · {yesterdayRecap.itemsSold} items
            </p>
          </>
        ) : (
          <>
            <p className="text-[13px] font-semibold text-text dark:text-[#E8E4DD] mt-1">No sales activity yesterday</p>
            <p className="text-[11px] text-text-muted dark:text-[#9B958E] mt-1">Stock movement data will appear here once POS syncs are active</p>
          </>
        )}
      </div>

      {/* Card 2: Overnight Alerts */}
      <div className={cn(
        'min-w-[195px] max-w-[230px] flex-1 shrink-0 p-4 rounded-2xl border border-bronze-200 dark:border-[#3A3836] relative overflow-hidden card-hover',
        overnightAlerts.count > 0 ? 'bg-warning/[0.04] dark:bg-warning/[0.06]' : 'bg-white dark:bg-[#2A2928]'
      )}>
        <div className={cn('absolute top-0 left-0 right-0 h-[3px]', overnightAlerts.count > 0 ? 'bg-warning' : 'bg-success')} />
        <p className={cn('text-[10px] font-semibold uppercase tracking-wider mb-1 flex items-center gap-1', overnightAlerts.count > 0 ? 'text-warning' : 'text-success')}>
          <svg className="w-3 h-3" viewBox="0 0 24 24" fill="currentColor"><path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z"/></svg>
          Overnight Alerts
        </p>
        {overnightAlerts.count > 0 ? (
          <>
            <p className="text-[13px] font-semibold text-text dark:text-[#E8E4DD] mt-0.5">
              {overnightAlerts.count} product{overnightAlerts.count > 1 ? 's' : ''} dropped below buffer stock
            </p>
            <ul className="mt-1.5 space-y-0.5">
              {overnightAlerts.products.slice(0, 3).map((name, i) => (
                <li key={i} className="text-[10px] text-text-muted dark:text-[#9B958E] flex items-start gap-1">
                  <span className="text-warning mt-0.5">·</span> {name}
                </li>
              ))}
            </ul>
          </>
        ) : (
          <>
            <p className="text-[13px] font-semibold text-text dark:text-[#E8E4DD] mt-1">All clear</p>
            <p className="text-[11px] text-text-muted dark:text-[#9B958E] mt-1">No products dropped below buffer overnight</p>
          </>
        )}
      </div>

      {/* Card 3: Deliveroo / Channel Risk */}
      <div className={cn(
        'min-w-[195px] max-w-[230px] flex-1 shrink-0 p-4 rounded-2xl border border-bronze-200 dark:border-[#3A3836] relative overflow-hidden card-hover',
        (deliverooRisk || hasChannelRisk)
          ? 'bg-error/[0.04] dark:bg-error/[0.06]'
          : 'bg-white dark:bg-[#2A2928]'
      )}>
        <div className={cn('absolute top-0 left-0 right-0 h-[3px]',
          (deliverooRisk || hasChannelRisk) ? 'bg-error' : 'bg-primary'
        )} />
        {deliverooRisk ? (
          <>
            <p className="text-[10px] font-semibold uppercase tracking-wider text-error mb-1">Deliveroo Risk</p>
            <p className="text-2xl font-bold text-error tracking-tight leading-tight">{deliverooRisk.outOfStock}</p>
            <p className="text-[11px] text-text-muted dark:text-[#9B958E] mt-0.5">
              out-of-stock items on Deliveroo (of {deliverooRisk.totalMapped})
            </p>
            <p className="text-[10px] text-text-muted dark:text-[#6B6560] mt-1">Customers can still order these</p>
            <a href="/channels" className="text-[10px] font-semibold text-error hover:text-error/80 mt-1.5 inline-block">Review unavailable items →</a>
          </>
        ) : hasChannelRisk ? (
          <>
            <p className="text-[10px] font-semibold uppercase tracking-wider text-error mb-1">Channel Risk</p>
            <div className="flex gap-4 mt-1">
              <div className="flex-1">
                <p className="text-2xl font-bold text-text dark:text-[#E8E4DD] tracking-tight leading-tight">{channelRisk.zeroStockCount}</p>
                <p className="text-[11px] text-text-muted dark:text-[#9B958E] mt-0.5">products with<br />zero stock</p>
              </div>
              <div className="flex-1">
                <p className="text-2xl font-bold text-text dark:text-[#E8E4DD] tracking-tight leading-tight">{channelRisk.negativeStockCount}</p>
                <p className="text-[11px] text-text-muted dark:text-[#9B958E] mt-0.5">products with<br />negative stock</p>
              </div>
            </div>
          </>
        ) : (
          <>
            <p className="text-[10px] font-semibold uppercase tracking-wider text-primary mb-1">Channel Risk</p>
            <p className="text-[13px] font-semibold text-text dark:text-[#E8E4DD] mt-1">No channels connected</p>
            <p className="text-[11px] text-text-muted dark:text-[#9B958E] mt-1">Connect a sales channel to see risk data</p>
          </>
        )}
      </div>

      {/* Card 4: Trending Up */}
      <div className={cn(
        'min-w-[195px] max-w-[230px] flex-1 shrink-0 p-4 rounded-2xl border border-bronze-200 dark:border-[#3A3836] relative overflow-hidden card-hover',
        trending ? 'bg-success/[0.04] dark:bg-success/[0.06]' : 'bg-white dark:bg-[#2A2928]'
      )}>
        <div className={cn('absolute top-0 left-0 right-0 h-[3px]', trending ? 'bg-success' : 'bg-primary')} />
        <p className={cn('text-[10px] font-semibold uppercase tracking-wider mb-1', trending ? 'text-success' : 'text-primary')}>Trending Up</p>
        {trending ? (
          <>
            <p className="text-[13px] font-semibold text-text dark:text-[#E8E4DD] mt-0.5 leading-tight">{trending.productName}</p>
            <p className="text-[18px] font-bold text-success mt-1">↑ {trending.units7d} units/wk</p>
            {trending.reorderDays !== null && (
              <p className="text-[10px] text-text-muted dark:text-[#6B6560] mt-1">
                Reorder in ~{trending.reorderDays} days at current velocity
              </p>
            )}
            {/* Mini sparkline SVG */}
            <svg className="w-full h-5 mt-1.5" viewBox="0 0 80 20" fill="none">
              <polyline points="0,18 12,15 24,16 36,12 48,9 56,5 64,3 72,2 80,1" stroke="currentColor" strokeWidth="1.5" className="text-success" />
            </svg>
          </>
        ) : (
          <>
            <p className="text-[13px] font-semibold text-text dark:text-[#E8E4DD] mt-1">No velocity data yet</p>
            <p className="text-[11px] text-text-muted dark:text-[#9B958E] mt-1">Trending products appear once stock movement is tracked over a few days</p>
          </>
        )}
      </div>

      {/* Card 5: Data Health */}
      <div className={cn(
        'min-w-[195px] max-w-[230px] flex-1 shrink-0 p-4 rounded-2xl border border-bronze-200 dark:border-[#3A3836] relative overflow-hidden card-hover',
        dataHealth.negativeStockCount > 0
          ? 'bg-[rgba(139,115,85,0.04)] dark:bg-[rgba(168,139,109,0.06)]'
          : 'bg-white dark:bg-[#2A2928]'
      )}>
        <div className="absolute top-0 left-0 right-0 h-[3px] bg-primary" />
        <p className="text-[10px] font-semibold uppercase tracking-wider text-primary mb-1">Data Health</p>
        {dataHealth.negativeStockCount > 0 ? (
          <>
            <p className="text-[13px] font-semibold text-text dark:text-[#E8E4DD] mt-0.5">
              {dataHealth.negativeStockCount} products have negative stock
            </p>
            <p className="text-[10px] text-text-muted dark:text-[#6B6560] mt-1">These items sold more than recorded. Count needed in-store.</p>
            <a href="/products" className="text-[10px] font-semibold text-primary hover:text-primary-dark mt-1.5 inline-block">Start stock count →</a>
          </>
        ) : (
          <>
            <p className="text-[13px] font-semibold text-success mt-1">All clear</p>
            <p className="text-[11px] text-text-muted dark:text-[#9B958E] mt-1">No negative stock levels detected</p>
          </>
        )}
      </div>
    </div>
  );
};

// ─── Sales Pulse KPIs ───────────────────────────────────────────────────────

const SalesPulseSection: React.FC<{ data: AIDashboardOverview['salesPulse'] }> = ({ data }) => {
  const hasData = data.ordersToday !== null;

  if (!hasData) {
    return (
      <div className="mb-7">
        <h2 className="text-[17px] font-bold text-text dark:text-[#E8E4DD] tracking-tight mb-3.5">Sales Pulse</h2>
        <div className="bg-white dark:bg-[#2A2928] border border-bronze-200 dark:border-[#3A3836] rounded-lg p-6 text-center">
          <p className="text-text-muted dark:text-[#9B958E] text-sm">Connect POS for sales data</p>
          <p className="text-[10px] text-text-muted dark:text-[#6B6560] mt-1">Sales estimates derived from stock movement will appear here</p>
        </div>
      </div>
    );
  }

  const dayName = new Date().toLocaleDateString('en-GB', { weekday: 'long' });
  const avg = data.sameDayAvg;
  const noSalesYet = data.ordersToday === 0;

  const avgLabel = avg?.label ?? dayName;
  const kpis = [
    {
      label: 'Orders Today',
      value: String(data.ordersToday),
      avg: avg ? `${avg.orders} avg ${avgLabel}` : null,
    },
    {
      label: 'Revenue Today',
      value: formatCurrency(data.revenueToday ?? 0),
      avg: avg ? `${formatCurrency(avg.revenue)} avg ${avgLabel}` : null,
    },
    {
      label: 'Items Sold',
      value: String(data.itemsSoldToday ?? 0),
      avg: avg ? `${avg.items} avg ${avgLabel}` : null,
    },
    {
      label: 'Avg Order Value',
      value: formatCurrency(data.avgOrderValue ?? 0),
      avg: avg && avg.orders > 0 ? `${formatCurrency(avg.revenue / avg.orders)} avg ${avgLabel}` : null,
    },
  ];

  return (
    <div className="mb-7">
      <h2 className="text-[17px] font-bold text-text dark:text-[#E8E4DD] tracking-tight mb-3.5">Sales Pulse</h2>
      {noSalesYet && (
        <p className="text-[11px] text-text-muted dark:text-[#9B958E] mb-2">No sales recorded yet today</p>
      )}
      <div className="grid grid-cols-4 gap-3">
        {kpis.map((kpi) => (
          <div key={kpi.label} className="bg-white dark:bg-[#2A2928] border border-bronze-200 dark:border-[#3A3836] rounded-lg p-4 card-hover">
            <p className="text-[11px] font-medium text-text-muted dark:text-[#9B958E] mb-1">{kpi.label}</p>
            <p className="text-[22px] font-bold text-text dark:text-[#E8E4DD] tracking-tight leading-tight">{kpi.value}</p>
            {kpi.avg && (
              <p className="text-[10px] text-text-muted dark:text-[#6B6560] mt-1.5">{kpi.avg}</p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

// ─── Stock-Out Countdown ────────────────────────────────────────────────────

const StockOutCountdown: React.FC<{
  items: AIDashboardOverview['stockOutCountdown'];
  negativeCount: number;
}> = ({ items, negativeCount }) => (
  <div className="flex flex-col">
    <h2 className="text-[17px] font-bold text-text dark:text-[#E8E4DD] tracking-tight mb-3.5">Stock-Out Countdown</h2>
    <div className="bg-white dark:bg-[#2A2928] border border-bronze-200 dark:border-[#3A3836] rounded-lg overflow-hidden flex-1">
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr>
              <th className="bg-background-alt dark:bg-[#222120] text-primary dark:text-[#A88B6D] font-semibold text-[10px] uppercase tracking-wider px-3.5 py-2.5 text-left border-b border-bronze-200 dark:border-[#3A3836]">Product</th>
              <th className="bg-background-alt dark:bg-[#222120] text-primary dark:text-[#A88B6D] font-semibold text-[10px] uppercase tracking-wider px-3.5 py-2.5 text-right border-b border-bronze-200 dark:border-[#3A3836]">Stock</th>
              <th className="bg-background-alt dark:bg-[#222120] text-primary dark:text-[#A88B6D] font-semibold text-[10px] uppercase tracking-wider px-3.5 py-2.5 text-right border-b border-bronze-200 dark:border-[#3A3836]">Velocity</th>
              <th className="bg-background-alt dark:bg-[#222120] text-primary dark:text-[#A88B6D] font-semibold text-[10px] uppercase tracking-wider px-3.5 py-2.5 text-right border-b border-bronze-200 dark:border-[#3A3836]">Days Left</th>
              <th className="bg-background-alt dark:bg-[#222120] text-primary dark:text-[#A88B6D] font-semibold text-[10px] uppercase tracking-wider px-3.5 py-2.5 text-left border-b border-bronze-200 dark:border-[#3A3836]">Status</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-3.5 py-6 text-center text-text-muted dark:text-[#9B958E]">
                  No products with active selling velocity
                </td>
              </tr>
            ) : (
              items.map((item, i) => (
                <tr key={i} className="hover:bg-background-alt dark:hover:bg-[#222120]">
                  <td className="px-3.5 py-2.5 border-b border-bronze-200 dark:border-[#3A3836]">
                    <div className="font-medium text-text dark:text-[#E8E4DD]">{item.name}</div>
                    <div className="text-[10px] text-text-muted dark:text-[#6B6560]">{item.sku}</div>
                  </td>
                  <td className="px-3.5 py-2.5 text-right border-b border-bronze-200 dark:border-[#3A3836] text-text dark:text-[#E8E4DD] font-medium tabular-nums">{item.currentStock}</td>
                  <td className="px-3.5 py-2.5 text-right border-b border-bronze-200 dark:border-[#3A3836] text-text-muted dark:text-[#9B958E] tabular-nums">{item.velocity}/day</td>
                  <td className="px-3.5 py-2.5 text-right border-b border-bronze-200 dark:border-[#3A3836] text-text dark:text-[#E8E4DD] font-medium tabular-nums">{item.daysLeft}</td>
                  <td className="px-3.5 py-2.5 border-b border-bronze-200 dark:border-[#3A3836]">
                    <StatusBadge status={item.status} />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      {negativeCount > 0 && (
        <div className="px-3.5 py-2.5 bg-primary/[0.04] dark:bg-[#A88B6D]/[0.06] flex items-center gap-2 text-primary dark:text-[#A88B6D] text-[11px] font-medium">
          <span className="w-4 h-4 rounded-full bg-primary dark:bg-[#A88B6D] text-white text-[10px] font-bold flex items-center justify-center shrink-0">i</span>
          {negativeCount} product{negativeCount !== 1 ? 's' : ''} have unknown stock levels
        </div>
      )}
    </div>
  </div>
);

// ─── Deliveroo Orders ───────────────────────────────────────────────────────

const DeliverooOrders: React.FC<{
  channels: AIDashboardOverview['channelRisk'];
}> = ({ channels }) => {
  const hasDeliveroo = channels.some(
    (ch) => ch.channelType === 'deliveroo'
  );

  return (
    <div className="flex flex-col">
      <h2 className="text-[17px] font-bold text-text dark:text-[#E8E4DD] tracking-tight mb-3.5">Deliveroo Orders</h2>
      <div className="bg-white dark:bg-[#2A2928] border border-bronze-200 dark:border-[#3A3836] rounded-lg p-5 flex-1 flex flex-col items-center justify-center text-center">
        {!hasDeliveroo ? (
          <>
            <div className="w-10 h-10 rounded-full bg-bronze-100 dark:bg-[#3A3836] flex items-center justify-center mb-3">
              <svg className="w-5 h-5 text-text-muted" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>
            </div>
            <p className="text-sm font-medium text-text dark:text-[#E8E4DD]">Connect Deliveroo</p>
            <p className="text-[11px] text-text-muted dark:text-[#9B958E] mt-1 max-w-[200px]">
              Connect Deliveroo in Channels to see live orders here
            </p>
            <a
              href="/channels"
              className="mt-3 inline-flex items-center gap-1 text-[11px] font-semibold text-primary dark:text-[#A88B6D] hover:text-primary-dark"
            >
              Go to Channels
              <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
            </a>
          </>
        ) : (
          <>
            <div className="w-10 h-10 rounded-full bg-success/10 flex items-center justify-center mb-3">
              <svg className="w-5 h-5 text-success" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
            </div>
            <p className="text-sm font-medium text-text dark:text-[#E8E4DD]">Deliveroo Connected</p>
            <p className="text-[11px] text-text-muted dark:text-[#9B958E] mt-1">Live order tracking coming soon</p>
          </>
        )}
      </div>
    </div>
  );
};

// ─── Channel Risk Radar ─────────────────────────────────────────────────────

const DONUT_COLORS = { out: '#B85C5C', low: '#C9A86C', healthy: '#5D8B55', needsCount: '#9B8EC4' };

// No more static placeholder channels — only show real connected channels

const ChannelDoughnutCard: React.FC<{
  channelName: string;
  subtitle: string;
  totalMapped: number;
  healthy: number;
  lowStock: number;
  outOfStock: number;
  negativeStock?: number;
  isStatic?: boolean;
}> = ({ channelName, subtitle, totalMapped, healthy, lowStock, outOfStock, negativeStock = 0, isStatic = false }) => {
  const pieData = [
    { name: 'Needs Count', value: negativeStock, color: DONUT_COLORS.needsCount },
    { name: 'Out of Stock', value: outOfStock, color: DONUT_COLORS.out },
    { name: 'Low Stock', value: lowStock, color: DONUT_COLORS.low },
    { name: 'Healthy', value: healthy, color: DONUT_COLORS.healthy },
  ].filter((d) => d.value > 0);

  return (
    <div className={`bg-white dark:bg-[#2A2928] border border-bronze-200 dark:border-[#3A3836] rounded-lg p-5${isStatic ? ' opacity-80' : ''}`}>
      <h3 className="text-sm font-bold text-text dark:text-[#E8E4DD] mb-0">{channelName}</h3>
      {isStatic && (
        <span className="text-[10px] text-text-muted dark:text-[#6B6560] italic">(not connected)</span>
      )}
      <p className="text-[11px] text-text-muted dark:text-[#6B6560] mb-3.5 mt-0.5">{subtitle}</p>
      <div className="flex items-center gap-5">
        <div className="w-[110px] h-[110px] relative shrink-0">
          {pieData.length > 0 ? (
            <DoughnutChart
              data={pieData.map(d => ({ value: d.value, color: d.color, label: d.name }))}
              size={110}
              thickness={18}
            />
          ) : (
            <div className="w-full h-full rounded-full border-4 border-bronze-200 dark:border-[#3A3836]" />
          )}
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <span className="text-2xl font-bold text-text dark:text-[#E8E4DD] tracking-tight leading-none">{totalMapped}</span>
            <span className="text-[9px] font-medium text-text-muted dark:text-[#6B6560] uppercase tracking-wider">ITEMS</span>
          </div>
        </div>
        <div className="flex-1 flex flex-col gap-2">
          <div className="flex justify-between text-xs">
            <span className="text-text-muted dark:text-[#9B958E]">Needs Count</span>
            <span className="font-semibold text-[#9B8EC4]">{negativeStock || 0}</span>
          </div>
          <div className="flex justify-between text-xs">
            <span className="text-text-muted dark:text-[#9B958E]">Out of Stock</span>
            <span className="font-semibold text-text dark:text-[#E8E4DD]">{outOfStock}</span>
          </div>
          <div className="flex justify-between text-xs">
            <span className="text-text-muted dark:text-[#9B958E]">Low Stock</span>
            <span className="font-semibold text-text dark:text-[#E8E4DD]">{lowStock}</span>
          </div>
          <div className="flex justify-between text-xs">
            <span className="text-text-muted dark:text-[#9B958E]">Healthy</span>
            <span className="font-semibold text-text dark:text-[#E8E4DD]">{healthy}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

const ChannelRiskRadar: React.FC<{
  channels: AIDashboardOverview['channelRisk'];
}> = ({ channels }) => {
  return (
    <div className="mb-7">
      <h2 className="text-[17px] font-bold text-text dark:text-[#E8E4DD] tracking-tight mb-3.5">Channel Risk Radar</h2>
      <div className="grid grid-cols-2 gap-3.5">
        {/* Live connected channels only */}
        {channels.map((ch) => (
          <ChannelDoughnutCard
            key={ch.channelName}
            channelName={ch.channelName}
            subtitle={ch.lastSync ? `Last sync ${formatRelativeTime(ch.lastSync)}` : 'Never synced'}
            totalMapped={ch.totalMapped}
            healthy={ch.healthy}
            lowStock={ch.lowStock}
            outOfStock={ch.outOfStock}
            negativeStock={ch.negativeStock || 0}
            isStatic={false}
          />
        ))}
        {channels.length === 0 && (
          <div className="col-span-2 bg-white dark:bg-[#2A2928] border border-bronze-200 dark:border-[#3A3836] rounded-lg p-6 text-center">
            <p className="text-text-muted dark:text-[#9B958E] text-sm">No channels with mapped products</p>
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Revenue Leaders + Margin Analysis ──────────────────────────────────────

const RevenueAndMargin: React.FC<{
  revenue: AIDashboardOverview['revenueLeaders'];
  margin: AIDashboardOverview['marginAnalysis'];
}> = ({ revenue, margin }) => (
  <div className="grid grid-cols-2 gap-3.5 mb-7">
    {/* Revenue Leaders */}
    <div className="bg-white dark:bg-[#2A2928] border border-bronze-200 dark:border-[#3A3836] rounded-lg p-5">
      <h3 className="text-sm font-bold text-text dark:text-[#E8E4DD] mb-3.5">Revenue Leaders (Last 7 days)</h3>
      {revenue.length === 0 ? (
        <p className="text-text-muted dark:text-[#9B958E] text-xs">No revenue data yet</p>
      ) : (
        <div className="flex flex-col gap-3">
          {revenue.map((item, i) => (
            <div key={i} className="flex items-center justify-between">
              <div className="min-w-0 flex-1 mr-3">
                <p className="text-xs font-medium text-text dark:text-[#E8E4DD] truncate">{item.name}</p>
                <p className="text-[10px] text-text-muted dark:text-[#6B6560]">{item.unitsSold} units sold</p>
              </div>
              <p className="text-xs font-bold text-text dark:text-[#E8E4DD] tabular-nums shrink-0">
                {formatCurrency(item.estimatedRevenue)}
              </p>
            </div>
          ))}
        </div>
      )}
    </div>

    {/* Margin Analysis */}
    <div className="bg-white dark:bg-[#2A2928] border border-bronze-200 dark:border-[#3A3836] rounded-lg p-5">
      <h3 className="text-sm font-bold text-text dark:text-[#E8E4DD] mb-3.5">Margin Leaders (Last 7 days)</h3>
      {margin.length === 0 ? (
        <p className="text-text-muted dark:text-[#9B958E] text-xs">No margin data yet</p>
      ) : (
        <div className="flex flex-col gap-3">
          {margin.map((item, i) => (
            <div key={i}>
              <div className="flex items-center justify-between mb-1">
                <p className="text-xs font-medium text-text dark:text-[#E8E4DD] truncate mr-3">{item.name}</p>
                <p className="text-xs font-bold text-text dark:text-[#E8E4DD] tabular-nums shrink-0">
                  {formatCurrency(item.totalMargin ?? item.marginAbs)}
                </p>
              </div>
              <div className="h-[5px] rounded-full bg-bronze-100 dark:bg-[#3A3836] overflow-hidden">
                <div
                  className="h-full rounded-full bg-primary dark:bg-[#A88B6D]"
                  style={{ width: `${Math.min(item.marginPct, 100)}%` }}
                />
              </div>
              <p className="text-[10px] text-text-muted dark:text-[#6B6560] mt-0.5">
                {item.marginPct}% margin · {item.unitsSold ?? 0} units sold
              </p>
            </div>
          ))}
        </div>
      )}
    </div>
  </div>
);

// ─── Reorder Priorities ─────────────────────────────────────────────────────

const StockStatusBadge: React.FC<{ status: 'OUT_OF_STOCK' | 'LOW' }> = ({ status }) => {
  const styles: Record<string, string> = {
    OUT_OF_STOCK: 'bg-warning/15 text-[#A08040] dark:text-warning font-semibold',
    LOW: 'bg-[#E8D44D]/10 text-[#8A7A20] dark:text-[#D4C44D]',
  };
  const labels: Record<string, string> = {
    OUT_OF_STOCK: 'OUT OF STOCK',
    LOW: 'LOW',
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium tracking-wide ${styles[status]}`}>
      {labels[status]}
    </span>
  );
};

const StockCorrections: React.FC<{
  items: AIDashboardOverview['stockCorrections'];
}> = ({ items }) => {
  if (items.length === 0) return null;

  return (
    <div className="mb-7">
      <h2 className="text-[17px] font-bold text-text dark:text-[#E8E4DD] tracking-tight mb-1 flex items-center gap-2">
        <svg className="w-[18px] h-[18px] text-[#B8860B] dark:text-[#D4A843]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        Stock Corrections Needed
      </h2>
      <p className="text-[11px] text-text-muted dark:text-[#9B958E] mb-3.5 ml-[26px]">
        These products show negative stock — go count them in-store and update the correct level
      </p>
      <div className="bg-[#FFFBF0] dark:bg-[#2E2A22] border border-[#D4A843]/30 dark:border-[#D4A843]/20 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr>
                {['Product', 'Current Stock', 'Buffer', 'Action'].map((h) => (
                  <th key={h} className={`bg-[#FFF7E0] dark:bg-[#2A2620] text-[#8B7335] dark:text-[#D4A843] font-semibold text-[10px] uppercase tracking-wider px-3.5 py-2.5 border-b border-[#D4A843]/20 dark:border-[#D4A843]/15 ${h === 'Current Stock' || h === 'Buffer' ? 'text-right' : 'text-left'} whitespace-nowrap`}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {items.map((item, i) => (
                <tr key={i} className="hover:bg-[#FFF5DB]/50 dark:hover:bg-[#33302A]">
                  <td className="px-3.5 py-2.5 border-b border-[#D4A843]/10 dark:border-[#D4A843]/10">
                    <div className="font-medium text-text dark:text-[#E8E4DD] truncate max-w-[260px]">{item.name}</div>
                    <div className="text-[10px] text-text-muted dark:text-[#6B6560]">{item.sku}</div>
                  </td>
                  <td className="px-3.5 py-2.5 border-b border-[#D4A843]/10 dark:border-[#D4A843]/10 text-right tabular-nums font-semibold text-error">{item.currentStock}</td>
                  <td className="px-3.5 py-2.5 border-b border-[#D4A843]/10 dark:border-[#D4A843]/10 text-right tabular-nums text-text-muted dark:text-[#9B958E]">{item.bufferStock}</td>
                  <td className="px-3.5 py-2.5 border-b border-[#D4A843]/10 dark:border-[#D4A843]/10">
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium tracking-wide bg-[#D4A843]/15 text-[#8B7335] dark:text-[#D4A843]">
                      <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                      </svg>
                      Count & correct
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const ReorderQueue: React.FC<{
  items: AIDashboardOverview['reorderQueue'];
}> = ({ items }) => (
  <div className="mb-7">
    <h2 className="text-[17px] font-bold text-text dark:text-[#E8E4DD] tracking-tight mb-3.5">Top Reorder Priorities</h2>
    <div className="bg-white dark:bg-[#2A2928] border border-bronze-200 dark:border-[#3A3836] rounded-lg overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr>
              {['Product', 'Status', 'Current', 'Buffer', 'Deficit', 'Revenue at Risk', 'Priority'].map((h) => (
                <th key={h} className={`bg-background-alt dark:bg-[#222120] text-primary dark:text-[#A88B6D] font-semibold text-[10px] uppercase tracking-wider px-3.5 py-2.5 border-b border-bronze-200 dark:border-[#3A3836] ${h === 'Revenue at Risk' ? 'text-right' : 'text-left'}`}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-3.5 py-6 text-center text-text-muted dark:text-[#9B958E]">
                  All products above buffer levels
                </td>
              </tr>
            ) : (
              items.map((item, i) => (
                <tr key={i} className="hover:bg-background-alt dark:hover:bg-[#222120]">
                  <td className="px-3.5 py-2.5 border-b border-bronze-200 dark:border-[#3A3836]">
                    <div className="font-medium text-text dark:text-[#E8E4DD]">{item.name}</div>
                    <div className="text-[10px] text-text-muted dark:text-[#6B6560]">{item.sku}</div>
                  </td>
                  <td className="px-3.5 py-2.5 border-b border-bronze-200 dark:border-[#3A3836]">
                    <StockStatusBadge status={item.stockStatus} />
                  </td>
                  <td className={`px-3.5 py-2.5 border-b border-bronze-200 dark:border-[#3A3836] tabular-nums font-medium ${item.currentStock === 0 ? 'text-[#A08040] dark:text-warning' : 'text-text dark:text-[#E8E4DD]'}`}>{item.currentStock}</td>
                  <td className="px-3.5 py-2.5 border-b border-bronze-200 dark:border-[#3A3836] text-text-muted dark:text-[#9B958E] tabular-nums">{item.bufferStock}</td>
                  <td className="px-3.5 py-2.5 border-b border-bronze-200 dark:border-[#3A3836] text-error font-semibold tabular-nums">-{item.deficit}</td>
                  <td className="px-3.5 py-2.5 border-b border-bronze-200 dark:border-[#3A3836] text-text dark:text-[#E8E4DD] tabular-nums text-right">{formatCurrency(item.estCost)}</td>
                  <td className="px-3.5 py-2.5 border-b border-bronze-200 dark:border-[#3A3836]">
                    <StatusBadge status={item.priority} />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  </div>
);

// ─── Chat Panel ─────────────────────────────────────────────────────────────

const SUGGESTION_PILLS = [
  "Show me today's tasks",
  'What should I reorder?',
  'Explain channel risk',
  'Summarise stock health',
];

const ChatPanel: React.FC = () => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  const sendMessage = useCallback(async (text: string) => {
    if (!text.trim() || isLoading) return;

    const userMessage: ChatMessage = { role: 'user', content: text.trim() };
    setMessages((prev) => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);

    try {
      const history = messages.length > 0 ? messages : undefined;
      const response = await aiDashboardApi.chat(text.trim(), history);
      setMessages((prev) => [...prev, response]);
    } catch {
      setMessages((prev) => [
        ...prev,
        { role: 'assistant', content: 'Sorry, I encountered an error. Please try again.' },
      ]);
    } finally {
      setIsLoading(false);
      inputRef.current?.focus();
    }
  }, [messages, isLoading]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    sendMessage(input);
  };

  return (
    <div className="w-[340px] border-l border-bronze-200 dark:border-[#3A3836] bg-white dark:bg-[#2A2928] flex-col hidden lg:flex">
      {/* Header */}
      <div className="p-4 border-b border-bronze-200 dark:border-[#3A3836]">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-primary/10 dark:bg-[#A88B6D]/15 flex items-center justify-center">
            <svg className="w-4 h-4 text-primary dark:text-[#A88B6D]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 3.104v5.714a2.25 2.25 0 01-.659 1.591L5 14.5M9.75 3.104c-.251.023-.501.05-.75.082m.75-.082a24.301 24.301 0 014.5 0m0 0v5.714c0 .597.237 1.17.659 1.591L19.8 15.3M14.25 3.104c.251.023.501.05.75.082M19.8 15.3l-1.57.393A9.065 9.065 0 0112 15a9.065 9.065 0 00-6.23.693L5 14.5m14.8.8l1.402 1.402c1.232 1.232.65 3.318-1.067 3.611A48.309 48.309 0 0112 21c-2.773 0-5.491-.235-8.135-.687-1.718-.293-2.3-2.379-1.067-3.61L5 14.5" />
            </svg>
          </div>
          <div>
            <h3 className="text-sm font-bold text-text dark:text-[#E8E4DD]">Ask StockClerk</h3>
            <p className="text-[10px] text-text-muted dark:text-[#6B6560]">AI-powered inventory assistant</p>
          </div>
        </div>
        {/* Suggestion pills */}
        <div className="flex flex-wrap gap-1.5 mt-3">
          {SUGGESTION_PILLS.map((pill) => (
            <button
              key={pill}
              onClick={() => sendMessage(pill)}
              disabled={isLoading}
              className="px-2.5 py-1 text-[10px] font-medium rounded-full border border-bronze-200 dark:border-[#3A3836] text-text-muted dark:text-[#9B958E] hover:bg-background-alt dark:hover:bg-[#222120] hover:text-text dark:hover:text-[#E8E4DD] transition-colors disabled:opacity-50"
            >
              {pill}
            </button>
          ))}
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3 custom-scrollbar">
        {messages.length === 0 && (
          <div className="flex flex-col items-center justify-center h-full text-center py-8">
            <div className="w-12 h-12 rounded-full bg-primary/10 dark:bg-[#A88B6D]/15 flex items-center justify-center mb-3">
              <svg className="w-6 h-6 text-primary dark:text-[#A88B6D]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
            </div>
            <p className="text-xs text-text-muted dark:text-[#9B958E]">
              Ask me anything about your inventory, channels, or operations.
            </p>
          </div>
        )}
        {messages.map((msg, i) => (
          <div
            key={i}
            className={cn(
              'max-w-[90%] rounded-xl px-3 py-2 text-xs leading-relaxed',
              msg.role === 'user'
                ? 'ml-auto bg-primary dark:bg-[#A88B6D] text-white dark:text-white rounded-br-sm'
                : 'bg-background-alt dark:bg-[#222120] text-text dark:text-[#E8E4DD] rounded-bl-sm'
            )}
          >
            {msg.role === 'user' ? (
              <div className="whitespace-pre-wrap">{msg.content}</div>
            ) : (
              <Markdown
                remarkPlugins={[remarkGfm]}
                components={{
                  table: ({ node: _node, ...props }) => (
                    <div className="overflow-x-auto -mx-1 my-2">
                      <table className="w-full text-[11px] border-collapse" {...props} />
                    </div>
                  ),
                  thead: (props) => (
                    <thead className="bg-background-alt dark:bg-[#1A1917]" {...props} />
                  ),
                  th: (props) => (
                    <th className="px-2 py-1.5 text-left font-semibold text-text-muted dark:text-[#9B958E] border-b border-bronze-200 dark:border-[#3A3836]" {...props} />
                  ),
                  td: ({ node: _node, ...props }) => (
                    <td className="px-2 py-1 border-b border-bronze-200/50 dark:border-[#3A3836]/50" {...props} />
                  ),
                  p: (props) => <p className="mb-1.5 last:mb-0" {...props} />,
                  strong: (props) => <strong className="font-semibold" {...props} />,
                  ul: (props) => <ul className="list-disc pl-4 mb-1.5 space-y-0.5" {...props} />,
                  ol: (props) => <ol className="list-decimal pl-4 mb-1.5 space-y-0.5" {...props} />,
                  h1: (props) => <h1 className="text-sm font-bold mb-1" {...props} />,
                  h2: (props) => <h2 className="text-[13px] font-bold mb-1" {...props} />,
                  h3: (props) => <h3 className="text-xs font-bold mb-1" {...props} />,
                  hr: () => <hr className="my-2 border-bronze-200 dark:border-[#3A3836]" />,
                  blockquote: (props) => (
                    <blockquote className="border-l-2 border-primary/30 pl-2 my-1.5 text-text-muted dark:text-[#9B958E] italic" {...props} />
                  ),
                  code: (props) => (
                    <code className="bg-bronze-100 dark:bg-[#1A1917] px-1 py-0.5 rounded text-[10px]" {...props} />
                  ),
                }}
              >
                {msg.content}
              </Markdown>
            )}
          </div>
        ))}
        {isLoading && (
          <div className="flex items-center gap-2 text-text-muted dark:text-[#9B958E]">
            <Spinner size="sm" />
            <span className="text-[10px]">Thinking...</span>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <form onSubmit={handleSubmit} className="p-3 border-t border-bronze-200 dark:border-[#3A3836]">
        <div className="flex items-center gap-2">
          <input
            ref={inputRef}
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Ask about your inventory..."
            disabled={isLoading}
            className="flex-1 text-xs bg-background-alt dark:bg-[#222120] border border-bronze-200 dark:border-[#3A3836] rounded-lg px-3 py-2 text-text dark:text-[#E8E4DD] placeholder:text-text-muted dark:placeholder:text-[#6B6560] focus:outline-none focus:ring-1 focus:ring-primary/30 disabled:opacity-50"
          />
          <button
            type="submit"
            disabled={isLoading || !input.trim()}
            className="shrink-0 w-8 h-8 rounded-lg bg-primary dark:bg-[#A88B6D] text-white flex items-center justify-center hover:bg-primary-dark disabled:opacity-50 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
            </svg>
          </button>
        </div>
      </form>
    </div>
  );
};

// ─── Mobile Chat FAB ────────────────────────────────────────────────────────

const MobileChatFab: React.FC<{ onClick: () => void }> = ({ onClick }) => (
  <button
    onClick={onClick}
    className="fixed bottom-6 right-6 w-14 h-14 rounded-full bg-primary dark:bg-[#A88B6D] text-white shadow-large flex items-center justify-center lg:hidden z-50 hover:bg-primary-dark transition-colors"
  >
    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
    </svg>
  </button>
);

// ─── Mobile Chat Slide-Over ─────────────────────────────────────────────────

const MobileChatOverlay: React.FC<{ isOpen: boolean; onClose: () => void }> = ({
  isOpen,
  onClose,
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 lg:hidden">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="absolute right-0 top-0 bottom-0 w-[340px] max-w-[90vw]">
        <div className="h-full flex flex-col">
          <div className="flex items-center justify-between p-3 bg-white dark:bg-[#2A2928] border-b border-bronze-200 dark:border-[#3A3836]">
            <span className="text-sm font-bold text-text dark:text-[#E8E4DD]">Ask StockClerk</span>
            <button onClick={onClose} className="p-1 text-text-muted hover:text-text">
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
            </button>
          </div>
          <div className="flex-1">
            <ChatPanel />
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Main Dashboard ─────────────────────────────────────────────────────────

const AIDashboard: React.FC = () => {
  const { user } = useAuth();
  const { data, isLoading, error, refetch } = useAIDashboardOverview();
  const [mobileChatOpen, setMobileChatOpen] = useState(false);

  if (isLoading) {
    return (
      <div className="flex h-[calc(100vh)] overflow-hidden">
        <div className="flex-1 overflow-y-auto p-6 pb-24 lg:pb-6">
          {/* Greeting skeleton */}
          <div className="mb-6">
            <div className="h-7 w-64 bg-bronze-200/60 dark:bg-[#3A3836] rounded-lg animate-pulse" />
            <div className="h-4 w-40 bg-bronze-200/40 dark:bg-[#3A3836]/60 rounded mt-2 animate-pulse" />
          </div>
          {/* Insight cards skeleton */}
          <div className="flex gap-3.5 overflow-hidden pb-1.5 mb-7">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="min-w-[195px] max-w-[230px] flex-1 shrink-0 h-24 rounded-2xl bg-bronze-200/40 dark:bg-[#3A3836] animate-pulse" />
            ))}
          </div>
          {/* Sales pulse skeleton */}
          <div className="h-48 rounded-lg bg-bronze-200/40 dark:bg-[#3A3836] animate-pulse mb-7" />
          {/* Two-column skeleton */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3.5 mb-7">
            <div className="h-40 rounded-lg bg-bronze-200/40 dark:bg-[#3A3836] animate-pulse" />
            <div className="h-40 rounded-lg bg-bronze-200/40 dark:bg-[#3A3836] animate-pulse" />
          </div>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="flex h-[calc(100vh)] overflow-hidden">
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <p className="text-error font-medium mb-2">Failed to load dashboard</p>
            <p className="text-text-muted text-sm mb-4">{(error as Error)?.message || 'Unknown error'}</p>
            <button onClick={() => refetch()} className="btn-primary px-4 py-2 text-sm rounded-lg">
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-[calc(100vh)] overflow-hidden">
      {/* Main scrollable content */}
      <div className="flex-1 overflow-y-auto p-6 pb-24 lg:pb-6 custom-scrollbar">
        {/* Greeting */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-text dark:text-[#E8E4DD] tracking-tight">
            {getGreeting()}, {user?.name || user?.email?.split('@')[0] || 'there'}
          </h1>
          <p className="text-[13px] text-text-muted dark:text-[#9B958E]">{formatDateLong()}</p>
        </div>

        {/* Insight Cards */}
        <InsightCards data={data.insightCards} />

        {/* Sales Pulse */}
        <SalesPulseSection data={data.salesPulse} />

        {/* Stock-Out Countdown + Deliveroo Orders */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3.5 mb-7">
          <StockOutCountdown
            items={data.stockOutCountdown}
            negativeCount={data.insightCards.dataHealth.negativeStockCount}
          />
          <DeliverooOrders channels={data.channelRisk} />
        </div>

        {/* Channel Risk Radar */}
        <ChannelRiskRadar channels={data.channelRisk} />

        {/* Revenue Leaders + Margin Analysis */}
        <RevenueAndMargin revenue={data.revenueLeaders} margin={data.marginAnalysis} />

        {/* Buffer Stock Optimization */}
        <div className="mb-7 bg-white dark:bg-[#2A2928] border border-bronze-200 dark:border-[#3A3836] rounded-lg p-5">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-[15px] font-bold text-text dark:text-[#E8E4DD] tracking-tight">Buffer Stock</h2>
              <p className="text-[11px] text-text-muted dark:text-[#9B958E] mt-0.5">
                All products currently use a flat buffer of 5. Ask the AI to optimize based on actual sales velocity.
              </p>
            </div>
            <button
              onClick={() => {
                // Find the chat input and trigger a message
                const chatInput = document.querySelector('input[placeholder*="inventory"]') as HTMLInputElement;
                if (chatInput) {
                  const nativeInputValueSetter = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, 'value')?.set;
                  nativeInputValueSetter?.call(chatInput, 'Calculate optimal buffer stock levels for my products and apply them');
                  chatInput.dispatchEvent(new Event('input', { bubbles: true }));
                  // Submit the form
                  const form = chatInput.closest('form');
                  form?.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
                }
              }}
              className="px-4 py-2 bg-primary text-white text-xs font-semibold rounded-lg hover:bg-primary-dark transition-colors shrink-0"
            >
              Optimize Buffers
            </button>
          </div>
        </div>

        {/* Stock Corrections (negative stock — data issues) */}
        <StockCorrections items={data.stockCorrections} />

        {/* Reorder Queue */}
        <ReorderQueue items={data.reorderQueue} />
      </div>

      {/* Chat Panel (desktop) */}
      <ChatPanel />

      {/* Mobile chat FAB + overlay */}
      <MobileChatFab onClick={() => setMobileChatOpen(true)} />
      <MobileChatOverlay
        isOpen={mobileChatOpen}
        onClose={() => setMobileChatOpen(false)}
      />
    </div>
  );
};

export default AIDashboard;
